import { LIFETIME_DATE } from "@onair/constants";
import {
	MyPaidSubscriptionsResponseDto,
	PaymentSummaryResponseDto,
	restRepository,
	SubscriptionEmailType,
	SubscriptionPaymentStatus,
	SubscriptionPeriodDetailsResponseDto,
	SubscriptionStatus,
} from "@onair/repositories";
import { formatCurrencyV2 } from "@onair/utils/format-number";
import dayjs from "dayjs";

export const getSubscriptionPeriodPaymentStatus = async (
	subscriptionPeriodId: string,
) => {
	const response =
		await restRepository.api.subscriptionControllerGetSubscriptionPeriodDetails(
			subscriptionPeriodId,
		);

	const data = response.data as SubscriptionPeriodDetailsResponseDto;

	// If API response indicates failure, throw an error to trigger the error UI
	if (!data.success) {
		throw new Error("API returned unsuccessful response");
	}

	// If periodDetails is missing, also throw an error
	if (!data.periodDetails) {
		throw new Error("Missing period details in API response");
	}

	const details = data.periodDetails;

	// Format end date, if endAt is LIFETIME_DATE then it means lifetime
	const endDate = details.endAt ? new Date(details.endAt) : null;
	let formattedEndDate = endDate
		? `Đến ${endDate.getDate()}/${endDate.getMonth() + 1}/${endDate.getFullYear()}`
		: "";
	if (formattedEndDate === `Đến ${LIFETIME_DATE}`) {
		formattedEndDate = "Không thời hạn";
	}

	// Format timestamp from paidAt
	const timestamp = details.paidAt
		? new Date(details.paidAt).toLocaleString("vi-VN")
		: new Date().toLocaleString("vi-VN");

	return {
		success: details.paymentStatus === SubscriptionPaymentStatus.PAID,
		paymentStatus: details.paymentStatus,
		packageName: details.membershipName || "OnAir Academy Membership",
		expiryDate: formattedEndDate,
		paymentMethod: details.paymentMethod || "Card",
		cardNumber: details.cardNumber || "",
		amount: details.invoiceAmount,
		timestamp: timestamp,
		nextBillingDate: details.nextBillingDate,
		nextBillingAmount: details.nextBillingAmount,
	};
};

export const getMyPaidSubscriptions = async () => {
	const response =
		await restRepository.api.subscriptionControllerGetMyPaidSubscriptions();

	const data = response.data as MyPaidSubscriptionsResponseDto;

	if (!data.success) {
		throw new Error("Failed to get paid subscriptions");
	}

	return {
		subscriptions: data.data,
		activeSubscription:
			data.data.find((sub) => sub.status === SubscriptionStatus.ACTIVE) ||
			data.data[0],
	};
};

export const getSubscriptionPaymentSummary = async (
	subscriptionId?: string,
) => {
	if (!subscriptionId) {
		throw new Error("Subscription ID is required");
	}

	const response =
		await restRepository.api.subscriptionControllerGetSubscriptionPaymentSummary(
			subscriptionId,
		);

	const data = response.data as PaymentSummaryResponseDto;

	// Format end date for subscription
	const endDate = data.subscription.expiryDate
		? new Date(data.subscription.expiryDate)
		: null;
	const formattedEndDate = endDate
		? `${dayjs(endDate).format("HH:mm")} ngày ${dayjs(endDate).format("DD/MM/YYYY")}`
		: "";

	const nextBillingDate = data.subscription.nextBillingDate
		? new Date(data.subscription.nextBillingDate)
		: null;

	const formattedNextBillingDate = nextBillingDate
		? `${dayjs(nextBillingDate).format("HH:mm")} ngày ${dayjs(nextBillingDate).format("DD/MM/YYYY")}`
		: "";

	return {
		paymentMethod: {
			id: data.paymentMethod.id,
			type: data.paymentMethod.type || "Card",
			lastDigits: data.paymentMethod.lastDigits?.slice(-4) || "****",
		},
		subscription: {
			id: data.subscription.id,
			status: data.subscription.status,
			membershipName: data.subscription.membershipName,
			price: formatCurrencyV2(data.subscription?.price, {}, true),
			nextBillingDate: formattedNextBillingDate,
			expiryDate: formattedEndDate,
			cancelledAt: data.subscription.cancelledAt,
			membershipPeriodValue: data.subscription.membershipPeriodValue,
			membershipPeriodType: data.subscription.membershipPeriodType,
		},
		billingRecords: data.paymentRecords.map((record) => ({
			...record,
			date: record.date,
			time: record.time,
			paymentAmount: record.paymentAmount,
			originalPrice: record.originalPrice,
			voucher: record.voucher?.code || null,
			totalDiscount: record.voucher?.totalDiscount || 0,
			status: record.status as SubscriptionPaymentStatus,
			membershipName: record.membershipName,
		})),
	};
};

export const createSubscription = async (
	membershipPriceId: string,
	voucher?: string,
) => {
	const response =
		await restRepository.api.subscriptionControllerCreateSubscription(
			membershipPriceId,
			{ voucher },
			{},
		);
	return response.data;
};

export const cancelSubscription = async (subscriptionId: string) => {
	const response =
		await restRepository.api.subscriptionControllerCancelSubscription(
			subscriptionId,
		);

	return response.data;
};

export const cancelNonPaidSubscription = async (subscriptionId: string) => {
	const response =
		await restRepository.api.subscriptionControllerCancelNonPaidSubscription(
			subscriptionId,
		);
	return response.data;
};

export const updatePaymentCard = async (
	subscriptionId: string,
	bankAccountId: string,
) => {
	const response =
		await restRepository.api.subscriptionControllerUpdateSubscriptionPaymentCard(
			subscriptionId,
			{ bankAccountId },
		);

	return response.data;
};

export const updateSubscriptionPlan = async (
	subscriptionId: string,
	membershipPriceId: string,
) => {
	const response =
		await restRepository.api.subscriptionControllerUpdateSubscriptionPlan(
			subscriptionId,
			membershipPriceId,
		);
	return response.data;
};

export const getPaymentRecords = async (
	subscriptionId: string,
	take: number = 10,
	skip: number = 0,
) => {
	const response =
		await restRepository.api.subscriptionControllerGetPaymentRecords(
			subscriptionId,
			{
				take,
				skip,
			},
		);

	const data = response.data;

	return {
		items: data.items?.map((record) => ({
			id: record.id,
			date: record.date,
			time: record.time,
			paymentAmount: record.paymentAmount,
			originalPrice: record.originalPrice,
			status: record.status as SubscriptionPaymentStatus,
			membershipName: record.membershipName,
			voucher: record.voucher?.code || null,
			totalDiscount: record.voucher?.totalDiscount || 0,
		})),
		meta: {
			total: data.total,
			skip: data.skip,
			take: data.take,
		},
	};
};

export interface IMySubscriptions {
	search?: string;
}

export const getMySubscriptions = async (params?: IMySubscriptions) => {
	const { data } = await restRepository.api.getMySubscriptions({
		search: params?.search || "",
	});

	return data;
};

export const createAcademySubscribeEmail = async (email: string) => {
	const response = await restRepository.api.createSubscriptionEmail({
		email,
	});
	return response.data;
};

export const getPaymentCards = async () => {
	const response =
		await restRepository.api.subscriptionControllerGetPaymentCards();
	return response.data;
};

export const addNewPaymentMethod = async () => {
	const response =
		await restRepository.api.subscriptionControllerAddNewPaymentMethod();
	return response.data;
};

export const applySubscriptionVoucher = async (
	membershipPriceId: string,
	voucher: string,
) => {
	const { data } =
		await restRepository.api.subscriptionControllerValidateVoucher(
			membershipPriceId,
			{
				voucher,
			},
		);

	return data;
};

export const subscriptionEmail = async (payload: {
	email: string;
	type: SubscriptionEmailType;
}) => {
	try {
		const response = await restRepository.api.createSubscriptionEmail({
			...payload,
		});
		return response.data;
	} catch {
		throw new Error("Failed");
	}
};
